import React, { useEffect, useMemo, useState } from 'react';
import { CountryMark, ServiceMark, SmsIcon } from '../components/sms/SmsMarks';
import {
  ApiError,
  createClientOperationKey,
  createPurchase,
  fetchCatalogServices,
  fetchCountries,
  fetchOperators,
  fetchRentAvailability,
  fetchRentCatalog,
  fetchCatalogInventory,
  CatalogInventory,
  getApiErrorMessage,
  getPurchaseQuote,
  PurchaseBuyResult,
  PurchaseQuoteResult,
  RentAvailability,
  RentDurationOption,
} from '../services/smsApi';
import {
  CatalogServiceItem,
  Country,
  DashboardSummary,
  NumberType,
} from '../types/models';
import { useDebouncedValue } from '../utils/debounce';
import { formatUsd } from '../utils/format';
import PurchaseSuccessModal from '../components/purchase/PurchaseSuccessModal';

interface BuyNumberProps {
  onPurchaseFinished: () => void;
  summary?: DashboardSummary | null;
}

const titleCollator = new Intl.Collator('ru', { sensitivity: 'base', numeric: true });

function getCountryTitle(country: Country): string {
  return country.titleRu || country.titleEn || String(country.id);
}

function getStockLabel(count?: number): string {
  if (count === undefined) return '—';
  if (count <= 0) return 'Нет в наличии';
  return `${count.toLocaleString('ru-RU')} шт.`;
}

function formatStockValue(count?: number | null): string {
  return count === undefined || count === null ? '—' : count.toLocaleString('ru-RU');
}


function formatRentDuration(hours: number): string {
  if (hours < 24) return `${hours} ч.`;
  const days = hours / 24;
  if (!Number.isInteger(days)) return `${hours} ч.`;
  if (days === 1) return '1 день';
  if (days >= 2 && days <= 4) return `${days} дня`;
  return `${days} дней`;
}

const BuyNumberRedesign: React.FC<BuyNumberProps> = ({ onPurchaseFinished, summary = null }) => {
  const [services, setServices] = useState<CatalogServiceItem[]>([]);
  const [popularServices, setPopularServices] = useState<CatalogServiceItem[]>([]);
  const [servicesTotal, setServicesTotal] = useState(0);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [serviceSearch, setServiceSearch] = useState('');
  const [selectedService, setSelectedService] = useState<CatalogServiceItem | null>(null);
  const [inventory, setInventory] = useState<CatalogInventory | null>(null);
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const [inventoryError, setInventoryError] = useState<string | null>(null);

  const [countries, setCountries] = useState<Country[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [countriesError, setCountriesError] = useState<string | null>(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);

  const [selectedOffer, setSelectedOffer] = useState<CatalogServiceItem | null>(null);
  const [offerLoading, setOfferLoading] = useState(false);
  const [offerError, setOfferError] = useState<string | null>(null);
  const [catalogRevision, setCatalogRevision] = useState(0);
  const [resolvedOfferKey, setResolvedOfferKey] = useState('');
  const numberType: NumberType = 'standard';
  const [selectedRentHours, setSelectedRentHours] = useState<number | null>(null);
  const [rentAvailability, setRentAvailability] = useState<RentAvailability | null>(null);
  const [rentOptions, setRentOptions] = useState<RentDurationOption[]>([]);
  const [rentOptionsLoading, setRentOptionsLoading] = useState(false);
  const [rentOptionsError, setRentOptionsError] = useState<string | null>(null);

  const [quantity, setQuantity] = useState(1);
  const [operator, setOperator] = useState('');
  const [operators, setOperators] = useState<string[]>([]);
  const [operatorsLoading, setOperatorsLoading] = useState(false);
  const [operatorsError, setOperatorsError] = useState<string | null>(null);
  const [operatorsKey, setOperatorsKey] = useState('');
  const [priceAfter, setPriceAfter] = useState(0);
  const [priceFailure, setPriceFailure] = useState<{ price: number; next: number | null; quantity: number } | null>(null);
  const [quote, setQuote] = useState<PurchaseQuoteResult | null>(null);
  const [resolvedQuoteKey, setResolvedQuoteKey] = useState<string | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [buyLoading, setBuyLoading] = useState(false);
  const [buyError, setBuyError] = useState<string | null>(null);
  const [buyResult, setBuyResult] = useState<PurchaseBuyResult | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [purchasedPhones, setPurchasedPhones] = useState<string[]>([]);
  const [operationKey, setOperationKey] = useState<string | null>(null);

  const debouncedServiceSearch = useDebouncedValue(serviceSearch, 250);
  const debouncedCountrySearch = useDebouncedValue(countrySearch, 150);

  useEffect(() => {
    let cancelled = false;
    setCountriesLoading(true);
    setCountriesError(null);
    fetchCountries({ visibleOnly: true })
      .then((items) => {
        if (cancelled) return;
        setCountries([...items].sort((left, right) => (
          titleCollator.compare(getCountryTitle(left), getCountryTitle(right))
        )));
      })
      .catch((error) => {
        if (!cancelled) setCountriesError(getApiErrorMessage(error));
      })
      .finally(() => {
        if (!cancelled) setCountriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchRentAvailability()
      .then((result) => {
        if (!cancelled) setRentAvailability(result);
      })
      .catch(() => {
        if (!cancelled) setRentAvailability(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setServicesLoading(true);
    setServicesError(null);
    fetchCatalogServices({
      all: true,
    }).then((result) => {
      if (cancelled) return;
      const nextServices = [...result.services].sort((left, right) => (
        titleCollator.compare(left.name || left.code, right.name || right.code)
      ));
      setServices(nextServices);
      setPopularServices(result.popularServices);
      setServicesTotal(result.total);
      setSelectedService((current) => (
        current ? nextServices.find((item) => item.code === current.code) || current : null
      ));
    }).catch((error) => {
      if (cancelled) return;
      setServices([]);
      setServicesTotal(0);
      setServicesError(getApiErrorMessage(error));
    }).finally(() => {
      if (!cancelled) setServicesLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [catalogRevision]);

  useEffect(() => {
    let cancelled = false;
    setInventoryLoading(true);
    setInventoryError(null);
    fetchCatalogInventory()
      .then((result) => {
        if (cancelled) return;
        setInventory(result);
      })
      .catch(() => {
        if (!cancelled) { setInventory(null); setInventoryError('Не удалось загрузить наличие номеров. Повторите загрузку.'); }
      })
      .finally(() => {
        if (!cancelled) setInventoryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [catalogRevision]);

  useEffect(() => {
    setSelectedOffer(null);
    setOfferError(null);
    if (!selectedService || !selectedCountry) {
      setOfferLoading(false);
      return undefined;
    }

    let cancelled = false;
    setOfferLoading(true);
    const requestKey = `${selectedService.code}:${selectedCountry.id}:${catalogRevision}`;
    fetchCatalogServices({
      countryId: selectedCountry.id,
      serviceCode: selectedService.code,
      limit: 1,
      offset: 0,
      topOnly: true,
    }).then((result) => {
      if (cancelled) return;
      setSelectedOffer(result.services[0] || null);
    }).catch((error) => {
      if (!cancelled) setOfferError(getApiErrorMessage(error));
    }).finally(() => {
      if (!cancelled) { setOfferLoading(false); setResolvedOfferKey(requestKey); }
    });

    return () => {
      cancelled = true;
    };
  }, [selectedService, selectedCountry, catalogRevision]);


  const operatorCountryId = selectedCountry?.id;
  const operatorServiceCode = selectedService?.code;
  useEffect(() => { setOperator(''); }, [operatorCountryId, operatorServiceCode]);

  useEffect(() => {
    setOperators([]);
    setOperatorsKey('');
    setOperatorsError(null);
    if (operatorCountryId === undefined || !operatorServiceCode) { setOperatorsLoading(false); return undefined; }
    let cancelled = false;
    setOperatorsLoading(true);
    fetchOperators(operatorCountryId, operatorServiceCode)
      .then((items) => {
        if (!cancelled) { setOperators(items); setOperatorsKey(`${operatorCountryId}:${operatorServiceCode}`); }
      })
      .catch(() => {
        if (!cancelled) { setOperators([]); setOperatorsError('Не удалось загрузить операторов с номерами. Можно выбрать «Любой оператор» или повторить загрузку.'); }
      }).finally(() => { if (!cancelled) setOperatorsLoading(false); });
    return () => {
      cancelled = true;
    };
  }, [operatorCountryId, operatorServiceCode, catalogRevision]);

  useEffect(() => { setPriceAfter(0); setPriceFailure(null); }, [selectedCountry?.id, selectedService?.code, operator]);

  useEffect(() => {
    setSelectedRentHours(null);
    setRentOptions([]);
    setRentOptionsError(null);
    if (!selectedCountry || !selectedService || !numberType) {
      setRentOptionsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setRentOptionsLoading(true);
    fetchRentCatalog(selectedCountry.id, selectedService.code)
      .then((rentServices) => {
        if (cancelled) return;
        const matched = rentServices.find((item) => item.code === selectedService.code);
        setRentOptions(matched?.options || []);
      })
      .catch((error) => {
        if (!cancelled) setRentOptionsError(getApiErrorMessage(error));
      })
      .finally(() => {
        if (!cancelled) setRentOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCountry, selectedService, numberType]);

  useEffect(() => {
    setBuyResult(null);
    setBuyError(null);
    setSuccessMessage(null);
    setOperationKey(null);
  }, [selectedCountry?.id, selectedService?.code, numberType, selectedRentHours, quantity, operator, priceAfter]);

  const filteredCountries = useMemo(() => {
    const needle = debouncedCountrySearch.trim().toLowerCase();
    return countries.filter((country) => !inventoryLoading && selectedService && (inventory?.countriesByService[selectedService.code]?.[country.id] ?? 0) > 0).filter((country) => (
      !needle
      || String(country.id).includes(needle)
      || country.titleRu.toLowerCase().includes(needle)
      || country.titleEn.toLowerCase().includes(needle)
    )).sort((left, right) => (
      titleCollator.compare(getCountryTitle(left), getCountryTitle(right))
    ));
  }, [countries, debouncedCountrySearch, inventory, inventoryLoading, selectedService]);

  const filteredServices = useMemo(() => {
    const needle = debouncedServiceSearch.trim().toLowerCase();
    return services.filter((service) => !needle || service.name.toLowerCase().includes(needle) || service.code.toLowerCase().includes(needle));
  }, [services, debouncedServiceSearch]);
  const currentOperators = operatorsKey === `${selectedCountry?.id}:${selectedService?.code}` ? operators : [];
  const isStandardActivation = selectedRentHours === null;
  const canQuote = Boolean(
    selectedService
    && selectedCountry
    && selectedOffer
    && numberType
    && isStandardActivation
    && (selectedOffer.count ?? 0) > 0
    && quantity > 0,
  );
  const quoteKey = canQuote
    ? [selectedCountry?.id, selectedService?.code, numberType, quantity, operator.trim(), priceAfter, 'activation-20m'].join(':')
    : null;
  const currentQuote = quoteKey && quoteKey === resolvedQuoteKey ? quote : null;
  const selectedFinal = selectedOffer?.finalPrice || 0;
  const selectedStock = selectedCountry && selectedService ? inventory?.countriesByService[selectedService.code]?.[selectedCountry.id] : undefined;
  const serviceAvailableTotal = selectedService ? inventory?.totals[selectedService.code] : undefined;
  const finalTotal = currentQuote?.finalTotal ?? selectedFinal * quantity;
  const canBuy = Boolean(canQuote && currentQuote && !quoteLoading && !priceFailure);

  useEffect(() => {
    if (!selectedCountry || !selectedService || !numberType || !canQuote) {
      setQuote(null);
      setResolvedQuoteKey(null);
      setQuoteError(null);
      setQuoteLoading(false);
      return undefined;
    }

    let cancelled = false;
    const nextQuoteKey = [
      selectedCountry.id,
      selectedService.code,
      numberType,
      quantity,
      operator.trim(),
      priceAfter,
      'activation-20m',
    ].join(':');
    setQuoteLoading(true);
    setQuoteError(null);
    getPurchaseQuote({
      serviceCode: selectedService.code,
      countryId: selectedCountry.id,
      operator: operator.trim() || undefined,
      quantity,
      numberType,
      ...(priceAfter > 0 ? { priceAfter } : {}),
    }).then((nextQuote) => {
      if (cancelled) return;
      setQuote(nextQuote);
      setResolvedQuoteKey(nextQuoteKey);
    }).catch((error) => {
      if (cancelled) return;
      setQuote(null);
      setResolvedQuoteKey(null);
      setQuoteError(getApiErrorMessage(error));
    }).finally(() => {
      if (!cancelled) setQuoteLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [canQuote, operator, priceAfter, numberType, quantity, selectedCountry, selectedService, catalogRevision]);

  const handleSelectService = (service: CatalogServiceItem) => {
    if (buyLoading) return;
    if (selectedService?.code === service.code) return;
    setSelectedService(service);
    setSelectedCountry(null);
    setCountrySearch('');
    setSelectedOffer(null);
  };

  const handleSelectCountry = (country: Country) => {
    if (buyLoading) return;
    if (selectedCountry?.id === country.id) return;
    setSelectedCountry(country);
    setSelectedOffer(null);
  };

  const handleBuy = async () => {
    if (!selectedCountry || !selectedService || !numberType || !currentQuote) return;
    setBuyLoading(true);
    setBuyError(null);
    setBuyResult(null);
    setSuccessMessage(null);
    try {
      const currentOperationKey = operationKey || createClientOperationKey('purchase');
      if (!operationKey) setOperationKey(currentOperationKey);
      const result = await createPurchase({
        serviceCode: selectedService.code,
        countryId: selectedCountry.id,
        operator: operator.trim() || undefined,
        quantity,
        numberType,
        ...(priceAfter > 0 ? { priceAfter } : {}),
      }, currentOperationKey, currentQuote);
      setBuyResult(result);
      if (result.successful.length > 0) {
        setPurchasedPhones(result.successful.map((item) => item.phone));
        onPurchaseFinished();
      }
      const canOfferNextPrice = result.terminal && result.pending.length === 0 && result.failed.length > 0
        && result.failed.every((failure) => failure.state === 'provider_rejected' && ['NO_NUMBERS', 'WRONG_MAX_PRICE'].includes(failure.code || ''));
      if (canOfferNextPrice) {
        setPriceFailure({ price: currentQuote.retailUnitPrice, next: currentQuote.nextFinalUnitPrice ?? null, quantity: result.failed.length });
        setBuyError(operator ? 'По этой цене у выбранного оператора номеров нет.' : 'По этой цене доступных номеров нет.');
        if (result.totals.successQty > 0) {
          setSuccessMessage(`Получено номеров: ${result.totals.successQty}. Следующая попытка будет только для оставшихся ${result.failed.length}.`);
        }
      } else if (!result.terminal) {
        setSuccessMessage('Операция принята и ещё обрабатывается. Повтор не создаст вторую покупку.');
      } else if (result.totals.successQty > 0) {
        setSuccessMessage(`Получено номеров: ${result.totals.successQty}. Сумма: ${formatUsd(result.totals.chargedTotal)}.`);
        setQuote(null);
        setResolvedQuoteKey(null);
        setCatalogRevision((current) => current + 1);
        setOperationKey(null);
      } else {
        const firstFailure = result.failed[0];
        if (['NO_NUMBERS', 'WRONG_MAX_PRICE', 'WRONG_OPERATOR', 'UNPROCESSABLE_ENTITY'].includes(firstFailure?.code || '')) {
          setQuote(null);
          setResolvedQuoteKey(null);
          setSelectedOffer(null);
          setCatalogRevision((current) => current + 1);
          setOperationKey(null);
          setBuyError(firstFailure?.reason || 'Номер сейчас недоступен. Деньги не списаны — обновите предложение или измените параметры.');
        } else {
          setBuyError(firstFailure?.reason || 'Покупка не завершена.');
        }
      }
    } catch (error) {
      const errorCode = error instanceof ApiError
        && error.details
        && typeof error.details === 'object'
        && 'code' in error.details
        ? String((error.details as { code?: unknown }).code || '')
        : '';
      if (errorCode === 'PRICE_CHANGED') {
        setQuote(null);
        setResolvedQuoteKey(null);
        setSelectedOffer(null);
        setCatalogRevision((current) => current + 1);
        setOperationKey(null);
      }
      setBuyError(error instanceof ApiError && error.status === 403
        ? 'Покупка временно выключена администратором.'
        : getApiErrorMessage(error));
    } finally {
      setBuyLoading(false);
    }
  };

  return (
    <div className="sms-pageStack sms-buyPage">
      <header className="sms-pageIntro">
        <div>
          <h1>Быстрая аренда номера</h1>
          <p>Выберите сервис и страну — наличие и цена проверятся автоматически.</p>
        </div>
      </header>

      <section className="sms-kpiGrid" aria-label="Показатели покупки">
        <article className="sms-kpiCard is-info">
          <span className="sms-kpiIcon" aria-hidden="true"><SmsIcon name="database" /></span>
          <div className="sms-kpiLabel">Доступно номеров</div>
          <div className="sms-kpiValue">{formatStockValue(selectedStock ?? (selectedService ? serviceAvailableTotal : null))}</div>
          <div className="sms-kpiHint">{selectedCountry ? getCountryTitle(selectedCountry) : selectedService ? `Для ${selectedService.name} во всех странах` : 'После выбора сервиса'}</div>
        </article>
        <article className="sms-kpiCard is-online">
          <span className="sms-kpiIcon" aria-hidden="true"><SmsIcon name="cloud" /></span>
          <div className="sms-kpiLabel">Срок активации</div>
          <div className="sms-kpiValue">20 минут</div>
          <div className="sms-kpiHint">После выдачи номера</div>
        </article>
        <article className="sms-kpiCard is-accent">
          <span className="sms-kpiIcon" aria-hidden="true"><SmsIcon name="activity" /></span>
          <div className="sms-kpiLabel">Активные номера</div>
          <div className="sms-kpiValue">{formatStockValue(summary?.activeNumbers)}</div>
          <div className="sms-kpiHint">Сейчас в работе</div>
        </article>
        <article className="sms-kpiCard is-balance">
          <span className="sms-kpiIcon" aria-hidden="true"><SmsIcon name="wallet" /></span>
          <div className="sms-kpiLabel">Баланс</div>
          <div className="sms-kpiValue">{summary ? formatUsd(summary.balance) : '—'}</div>
          <div className="sms-kpiHint">Общий баланс портала</div>
        </article>
      </section>

      <div className="sms-purchaseLayout">
        <section className="sms-card sms-purchaseWorkbench">
          <div className="sms-pickerGrid">
            <div className="sms-pickerColumn">
              <div className="sms-pickerHead">
                <div><span>1</span><h2>Сервис</h2></div>
                <small>{selectedService?.name || 'Не выбран'}</small>
              </div>
              {popularServices.length > 0 ? (
                <div className="sms-quickRent__popular" role="group" aria-labelledby="quick-rent-popular-title">
                  <strong id="quick-rent-popular-title">Популярные</strong>
                  <div>
                    {popularServices.map((service) => {
                      const isSelected = selectedService?.code === service.code;
                      return (
                        <button
                          key={service.code}
                          type="button"
                          className={isSelected ? 'is-selected' : ''}
                          aria-pressed={isSelected}
                          onClick={() => handleSelectService(service)}
                        >
                          <ServiceMark code={service.code} name={service.name} />
                          <span>{service.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              <label className="sms-searchField">
                <SmsIcon name="search" />
                <span className="sr-only">Поиск сервиса</span>
                <input className="u2-input" type="search" name="service-search" autoComplete="off" aria-label="Поиск сервиса" value={serviceSearch} onChange={(event) => setServiceSearch(event.target.value)} placeholder="Поиск сервиса" />
              </label>
              {servicesLoading ? <div className="sms-pickerStatus">Загружаем сервисы…</div> : null}
              {servicesError ? <div className="sms-feedback is-error">{servicesError}</div> : null}
              <p className="sms-fieldHint">Сервисов: {filteredServices.length} из {servicesTotal}. Поиск по всему списку.</p>
              {inventoryLoading ? <div className="sms-pickerStatus" role="status">Загружаем наличие…</div> : null}
              {inventoryError ? <div className="sms-feedback is-warning" role="status">{inventoryError}<button className="u2-btn" type="button" onClick={() => setCatalogRevision((value) => value + 1)}>Повторить загрузку наличия</button></div> : null}
              <div className="sms-optionList is-services" role="listbox" aria-label="Сервисы">
                {filteredServices.map((service) => {
                  const isSelected = selectedService?.code === service.code;
                  const serviceOffer = selectedOffer?.code === service.code ? selectedOffer : null;
                  const serviceStock = !inventory || inventoryLoading ? undefined : selectedCountry
                    ? inventory.countriesByService[service.code]?.[selectedCountry.id] ?? 0 : inventory.totals[service.code] ?? 0;
                  return (
                    <button type="button" role="option" aria-selected={isSelected} key={service.code} className={`sms-optionButton${isSelected ? ' is-selected' : ''}`} onClick={() => handleSelectService(service)}>
                      <ServiceMark code={service.code} name={service.name} />
                      <span className="sms-optionMain"><strong>{service.name}</strong><small>{selectedCountry ? getCountryTitle(selectedCountry) : 'Все страны'}</small></span>
                      <span className="sms-optionMeta">
                        <strong>{inventoryLoading ? '…' : getStockLabel(serviceStock)}</strong>
                        <small>{serviceOffer?.finalPrice ? `от ${formatUsd(serviceOffer.finalPrice)}` : inventoryError ? 'наличие не загружено' : 'номеров в каталоге'}</small>
                      </span>
                      <span className="sms-choiceIndicator" aria-hidden="true" />
                    </button>
                  );
                })}
                {!servicesLoading && filteredServices.length === 0 ? <div className="sms-pickerStatus">Сервис не найден.</div> : null}
              </div>
            </div>

            <div className={`sms-pickerColumn${!selectedService ? ' is-disabled' : ''}`}>
              <div className="sms-pickerHead">
                <div><span>2</span><h2>Страна</h2></div>
                <small>{selectedCountry ? getCountryTitle(selectedCountry) : 'Не выбрана'}</small>
              </div>
              <label className="sms-searchField">
                <SmsIcon name="search" />
                <span className="sr-only">Поиск страны</span>
                <input className="u2-input" type="search" name="country-search" autoComplete="off" aria-label="Поиск страны" value={countrySearch} onChange={(event) => setCountrySearch(event.target.value)} placeholder="Поиск страны" disabled={!selectedService} />
              </label>
              {!selectedService ? <div className="sms-pickerStatus">Сначала выберите сервис.</div> : null}
              {selectedService && (countriesLoading || inventoryLoading) ? <div className="sms-pickerStatus">Загружаем страны с номерами…</div> : null}
              {countriesError ? <div className="sms-feedback is-error">{countriesError}</div> : null}
              <div className="sms-optionList is-countries" role="listbox" aria-label="Страны">
                {selectedService ? filteredCountries.map((country) => {
                  const isSelected = selectedCountry?.id === country.id;
                  const count = inventory?.countriesByService[selectedService.code]?.[country.id];
                  return (
                    <button type="button" role="option" aria-selected={isSelected} key={country.id} className={`sms-optionButton${isSelected ? ' is-selected' : ''}`} onClick={() => handleSelectCountry(country)}>
                      <CountryMark name={country.titleEn || getCountryTitle(country)} />
                      <span className="sms-optionMain"><strong>{getCountryTitle(country)}</strong><small>{country.titleEn !== getCountryTitle(country) ? country.titleEn : `Код ${country.id}`}</small></span>
                      <span className="sms-optionMeta">
                        <strong>{formatStockValue(count)}</strong>
                        <small>номеров в каталоге</small>
                      </span>
                      <span className="sms-choiceIndicator" aria-hidden="true" />
                    </button>
                  );
                }) : null}
                {selectedService && !countriesLoading && !inventoryLoading && !inventoryError && filteredCountries.length === 0 ? <div className="sms-pickerStatus">{debouncedCountrySearch ? 'Страна с номерами не найдена.' : 'Для этого сервиса сейчас нет стран с номерами.'}</div> : null}
              </div>
            </div>
          </div>
          {offerLoading ? <div className="sms-pickerStatus" role="status">Проверяем наличие и цену…</div> : null}
          {offerError ? <div className="sms-feedback is-error">{offerError}</div> : null}
          {selectedCountry && !offerLoading && !offerError && !selectedOffer && resolvedOfferKey === `${selectedService?.code}:${selectedCountry.id}:${catalogRevision}` ? <div className="sms-feedback is-warning">{buyResult?.successful.length ? 'Покупка завершена. Для новой покупки в этой стране пока нет доступных предложений.' : 'Для выбранного сервиса в этой стране сейчас нет доступного предложения.'}</div> : null}
        </section>

        <aside className="sms-card sms-checkoutCard">
          <div className="sms-sectionHead"><h2>Выбранное предложение</h2><p>Сервис → страна → оператор → срок</p></div>
          <dl className="sms-orderSummary">
            <div><dt>Сервис</dt><dd>{selectedService ? <span className="sms-summaryEntity"><ServiceMark code={selectedService.code} name={selectedService.name} />{selectedService.name}</span> : '—'}</dd></div>
            <div><dt>Страна</dt><dd>{selectedCountry ? <span className="sms-summaryEntity"><CountryMark name={selectedCountry.titleEn || getCountryTitle(selectedCountry)} />{getCountryTitle(selectedCountry)}</span> : '—'}</dd></div>
            <div><dt>Номеров в общем предложении</dt><dd>{getStockLabel(selectedStock)}</dd></div>
          </dl>

          <label className="sms-field">
            <span>Оператор</span>
            <select aria-label="Оператор" name="operator" className="u2-input" value={operator}
              disabled={!selectedCountry || operatorsLoading || buyLoading}
              onChange={(event) => { if (priceFailure) setQuantity(priceFailure.quantity); setOperator(event.target.value); }}>
              <option value="">{operatorsLoading ? 'Загружаем операторов…' : 'Любой оператор'}</option>
              {currentOperators.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
          {operatorsError ? <div className="sms-feedback is-warning" role="status">{operatorsError}<button type="button" className="u2-btn" onClick={() => setCatalogRevision((value) => value + 1)}>Повторить загрузку</button></div> : null}
          {selectedCountry && !operatorsLoading && !operatorsError ? <p className="sms-fieldHint">{currentOperators.length ? 'Показаны операторы с остатком в каталоге. Доступность по выбранной цене проверяется при выдаче.' : 'Отдельных операторов с номерами сейчас нет. Доступен поиск по всем операторам.'}</p> : null}

          <div className="sms-rentPeriod">
            <div className="sms-rentPeriod__head"><strong>Период аренды</strong><span>{selectedOffer ? (selectedRentHours === null ? '20 минут' : formatRentDuration(selectedRentHours)) : 'Сначала выберите предложение'}</span></div>
            <div className="sms-durationOptions" role="radiogroup" aria-label="Срок использования">
              <button type="button" role="radio" aria-checked={selectedRentHours === null} disabled={!selectedOffer || buyLoading} className={selectedRentHours === null ? 'is-selected' : ''} onClick={() => setSelectedRentHours(null)}>20 минут</button>
              {rentOptions.map((option) => (
                <button type="button" role="radio" disabled={buyLoading} aria-checked={selectedRentHours === option.hours} key={option.hours} className={selectedRentHours === option.hours ? 'is-selected' : ''} onClick={() => setSelectedRentHours(option.hours)}>{formatRentDuration(option.hours)}</button>
              ))}
            </div>
            {selectedOffer && rentOptionsLoading ? <p className="sms-fieldHint">Проверяем сроки у поставщика…</p> : null}
            {rentOptionsError ? <p className="sms-fieldHint">Не удалось получить дополнительные сроки: {rentOptionsError}</p> : null}
            {selectedOffer && !rentOptionsLoading && rentOptions.length === 0 ? <p className="sms-fieldHint">Для этого предложения доступна активация на 20 минут.</p> : null}
            {selectedRentHours !== null ? <div className="sms-feedback is-warning"><strong>Долгая аренда пока недоступна для покупки</strong><span>{rentAvailability?.reason || 'Поставщик не подтвердил полный цикл управления арендой.'}</span></div> : null}
          </div>

          <div className="sms-checkoutFields">
            <label className="sms-field"><span>Количество</span><input className="u2-input" type="number" name="quantity" inputMode="numeric" autoComplete="off" min={1} max={10} value={quantity} disabled={buyLoading || Boolean(priceFailure)} onChange={(event) => { const value = Number(event.target.value); if (Number.isFinite(value)) setQuantity(Math.max(1, Math.min(10, Math.floor(value)))); }} /></label>
          </div>

          <div className="sms-checkoutTotal"><span>Итого</span><strong>{currentQuote && isStandardActivation ? formatUsd(finalTotal) : '—'}</strong><small>{currentQuote && isStandardActivation ? `${quantity} × ${formatUsd(currentQuote.finalUnitPrice)}` : 'Цена появится после проверки параметров'}</small></div>
          <div className="sms-autoQuote" aria-live="polite">
            {!isStandardActivation ? rentAvailability?.reason || 'Долгая аренда временно недоступна.' : null}
            {isStandardActivation && quoteLoading ? 'Проверяем актуальную цену…' : null}
            {isStandardActivation && !quoteLoading && currentQuote && !priceFailure ? (
              currentQuote.recentlyUnavailableFinalUnitPrice
                ? `По цене до ${formatUsd(currentQuote.recentlyUnavailableFinalUnitPrice)} номера только что не выдавались — показываем следующую доступную цену.`
                : 'Цена проверена и готова к покупке.'
            ) : null}
            {isStandardActivation && !quoteLoading && !currentQuote && !quoteError ? 'Завершите выбор параметров.' : null}
          </div>
          <button type="button" className="u2-btn is-primary sms-buyButton" onClick={handleBuy} disabled={!canBuy || buyLoading}>
            {buyLoading ? 'Покупаем…' : !isStandardActivation ? 'Долгая аренда временно недоступна' : currentQuote ? `Купить за ${formatUsd(finalTotal)}` : quoteLoading ? 'Проверяем цену…' : 'Купить номер'}
          </button>
          {quoteError ? <div className="sms-feedback is-error">{quoteError}</div> : null}
          {buyError ? <div className="sms-feedback is-error" role="alert">{buyError}</div> : null}
          {priceFailure ? <div className="sms-feedback is-warning" role="status">
            <p>За невыданные номера деньги не списаны. Можно проверить следующую цену или сменить оператора.</p>
            {priceFailure.next !== null ? <button type="button" className="u2-btn is-primary" onClick={() => {
              setQuantity(priceFailure.quantity); setPriceAfter(priceFailure.price); setPriceFailure(null); setOperationKey(null);
            }}>Следующая цена — {formatUsd(priceFailure.next)} за номер</button> : <p>Следующих цен в текущем предложении нет.</p>}
            {operator ? <button type="button" className="u2-btn" onClick={() => { setQuantity(priceFailure.quantity); setOperator(''); }}>Выбрать любой оператор</button> : null}
            <button type="button" className="u2-btn" onClick={() => document.querySelector<HTMLSelectElement>('select[name="operator"]')?.focus()}>Сменить оператора</button>
          </div> : null}
          {successMessage ? <div className="sms-feedback is-success">{successMessage}</div> : null}
          {buyResult && buyResult.failed.length > 0 ? <div className="sms-feedback is-error">Неуспешных попыток: {buyResult.failed.length}</div> : null}
          <p className="sms-priceNote">Начинаем с минимальной доступной цены. Наличие у выбранного оператора по этой цене подтверждается при выдаче. Повышение цены — только после вашего подтверждения.</p>
        </aside>
      </div>
      {purchasedPhones.length > 0 ? <PurchaseSuccessModal phones={purchasedPhones} onClose={() => setPurchasedPhones([])} /> : null}
    </div>
  );
};

export default BuyNumberRedesign;
